import { documentHeaders, admitBrowserDocument } from '../utils/browserPresence';
import React, { useEffect, useRef, useState } from 'react';
import { Video, VideoOff, Mic, MicOff, PhoneOff, AlertCircle, Loader } from 'lucide-react';

/**
 * Native WebRTC Video Call Component
 * Fully FREE implementation without external dependencies
 * DPDPA compliant with direct peer-to-peer connection
 */
const WebRTCVideoCall = ({ 
  roomName, 
  userDisplayName, 
  userEmail,
  onCallEnd,
  consultationId,
  videoCallId, // video call ID from backend
  isInitiator // true for psychologist, false for client
}) => {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const processedMessagesRef = useRef(new Set()); // Track processed messages
  const callStartTimeRef = useRef(null); // Track when call started

  const [isConnecting, setIsConnecting] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [remoteUserConnected, setRemoteUserConnected] = useState(false);

  // Get backend API URL
  const API = process.env.REACT_APP_BACKEND_URL + '/api' || '';
  const WS_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');

  // ICE servers configuration (using free STUN servers)
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }
    ]
  };

  useEffect(() => {
    // Record call start time
    callStartTimeRef.current = new Date().toISOString();
    console.log('📞 Call start time recorded:', callStartTimeRef.current);
    
    initializeVideoCall();

    return () => {
      cleanup();
    };
  }, []);

  // Transcription functionality removed

  const initializeVideoCall = async () => {
    try {
      console.log('🎥 Initializing WebRTC video call...');
      
      // Get user media (camera and microphone)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      });

      console.log('✅ Got local media stream');
      console.log('Stream ID:', stream.id);
      console.log('Stream tracks:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
      
      localStreamRef.current = stream;
      
      // CRITICAL: Store globally for aggressive cleanup
      window.localStreamGlobal = stream;
      console.log('✅ Stream stored globally for cleanup');

      // Display local video
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Create peer connection
      peerConnectionRef.current = new RTCPeerConnection(iceServers);

      // Add local stream tracks to peer connection
      stream.getTracks().forEach(track => {
        peerConnectionRef.current.addTrack(track, stream);
      });

      // Handle incoming remote stream
      peerConnectionRef.current.ontrack = (event) => {
        console.log('📹 Received remote track:', event.track.kind, 'from stream:', event.streams[0].id);
        console.log('Remote stream tracks:', event.streams[0].getTracks().map(t => `${t.kind}: ${t.id}`));
        
        if (remoteVideoRef.current && event.streams[0]) {
          console.log('Setting remote video srcObject');
          remoteVideoRef.current.srcObject = event.streams[0];
          setRemoteUserConnected(true);
          console.log('✅ Remote user connected and video set');
        } else {
          console.error('❌ Cannot set remote video - ref or stream missing');
        }
      };

      // Handle ICE candidates
      peerConnectionRef.current.onicecandidate = (event) => {
        if (event.candidate) {
          console.log('🧊 Sending ICE candidate');
          sendSignal({
            type: 'ice-candidate',
            candidate: event.candidate,
            from: userDisplayName
          });
        }
      };

      // Handle connection state changes
      peerConnectionRef.current.onconnectionstatechange = () => {
        const state = peerConnectionRef.current.connectionState;
        console.log('🔗 Connection state:', state);
        console.log('ICE connection state:', peerConnectionRef.current.iceConnectionState);
        console.log('ICE gathering state:', peerConnectionRef.current.iceGatheringState);
        console.log('Signaling state:', peerConnectionRef.current.signalingState);
        
        if (state === 'connected') {
          setIsConnected(true);
          setIsConnecting(false);
          console.log('✅ Peer connection established!');
        } else if (state === 'failed' || state === 'disconnected') {
          console.error('❌ Connection failed or disconnected');
          setError('Connection lost. Please refresh and try again.');
        }
      };

      // Start HTTP polling for signaling
      startPolling();

      // Audio recording functionality removed

    } catch (err) {
      console.error('❌ Failed to initialize video call:', err);
      let errorMessage = 'Failed to access camera or microphone. ';
      
      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera and microphone permissions in your browser.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera or microphone found. Please connect a device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera or microphone is already in use by another application.';
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }
      
      setError(errorMessage);
      setIsConnecting(false);
    }
  };

  const sendSignal = async (signal) => {
    try {
      console.log('📤 Sending signal:', signal.type, 'from:', userDisplayName);
      await admitBrowserDocument(API, sessionStorage.getItem('token'));
      await fetch(`${API}/video-calls/signal/${roomName}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('token')}`, ...documentHeaders() },
        body: JSON.stringify(signal)
      });
      console.log('✅ Signal sent successfully');
    } catch (err) {
      console.error('❌ Error sending signal:', err);
    }
  };

  const startPolling = () => {
    console.log('🔄 Starting HTTP polling for signals...');
    
    // If initiator (psychologist), create offer after a short delay
    if (isInitiator) {
      setTimeout(() => {
        console.log('👨‍⚕️ Psychologist creating offer...');
        createOffer();
      }, 1000);
    } else {
      console.log('👤 Client waiting for offer...');
    }

    // Poll for new messages every 1 second
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const url = lastTimestampRef.current 
          ? `${API}/video-calls/signal/${roomName}?since=${lastTimestampRef.current}`
          : `${API}/video-calls/signal/${roomName}`;
        
        await admitBrowserDocument(API, sessionStorage.getItem('token'));
        const response = await fetch(url, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}`, ...documentHeaders() } });
        const data = await response.json();

        if (data.messages && data.messages.length > 0) {
          console.log(`📥 Polling received ${data.messages.length} messages`);
          
          for (const message of data.messages) {
            // Update last timestamp
            if (message.timestamp) {
              lastTimestampRef.current = message.timestamp;
            }

            // Create unique message ID to prevent duplicate processing
            const messageId = `${message.type}-${message.from}-${message.timestamp || Date.now()}`;
            
            // Check if we already processed this message
            if (processedMessagesRef.current.has(messageId)) {
              console.log('⏭️ Skipping already processed message:', messageId);
              continue;
            }

            console.log('Processing message:', message.type, 'from:', message.from, 'my name:', userDisplayName);

            // Skip messages from self (exact match or includes)
            if (message.from === userDisplayName || message.from?.includes(userDisplayName) || userDisplayName?.includes(message.from)) {
              console.log('⏭️ Skipping self message');
              processedMessagesRef.current.add(messageId); // Mark as processed
              continue;
            }

            console.log('📨 Received message:', message.type, 'from:', message.from);

            // Mark as processed before handling to prevent race conditions
            processedMessagesRef.current.add(messageId);
            
            // Clean up old processed messages (keep last 50)
            if (processedMessagesRef.current.size > 50) {
              const entries = Array.from(processedMessagesRef.current);
              processedMessagesRef.current = new Set(entries.slice(-50));
            }

            switch (message.type) {
              case 'offer':
                console.log('📥 Handling offer...');
                await handleOffer(message.offer);
                break;
              case 'answer':
                console.log('📥 Handling answer...');
                await handleAnswer(message.answer);
                break;
              case 'ice-candidate':
                console.log('🧊 Handling ICE candidate...');
                await handleIceCandidate(message.candidate);
                break;
              case 'end-call':
                console.log('📴 Other party ended the call');
                
                // Stop polling immediately
                if (pollingIntervalRef.current) {
                  clearInterval(pollingIntervalRef.current);
                  pollingIntervalRef.current = null;
                  console.log('⏹️ Polling stopped');
                }
                
                setError(`${message.from} has ended the call.`);
                cleanup();
                
                // Auto-close after 3 seconds and pass flag that remote ended the call
                setTimeout(() => {
                  if (onCallEnd) onCallEnd(true); // true = remote ended call
                }, 3000);
                break;
              default:
                console.log('❓ Unknown message type:', message.type);
            }
          }
        }
      } catch (err) {
        console.error('❌ Error polling signals:', err);
      }
    }, 1000);
  };

  const createOffer = async () => {
    try {
      console.log('📤 Creating offer...');
      const offer = await peerConnectionRef.current.createOffer();
      console.log('Offer created:', offer.type, offer.sdp.substring(0, 50) + '...');
      
      await peerConnectionRef.current.setLocalDescription(offer);
      console.log('Local description set');

      await sendSignal({
        type: 'offer',
        offer: offer,
        from: userDisplayName
      });
      console.log('✅ Offer sent to signaling server');
    } catch (err) {
      console.error('❌ Error creating offer:', err);
    }
  };

  const handleOffer = async (offer) => {
    try {
      console.log('📥 Handling offer...', offer.type);
      console.log('Current signaling state:', peerConnectionRef.current.signalingState);
      
      // CRITICAL: Check if we're in the right state to receive an offer
      if (peerConnectionRef.current.signalingState !== 'stable') {
        console.warn('⚠️ Ignoring offer - not in stable state. Current state:', peerConnectionRef.current.signalingState);
        console.warn('This usually means we already processed an offer or are in the middle of negotiation');
        return;
      }
      
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('Remote description set from offer');
      console.log('New signaling state:', peerConnectionRef.current.signalingState);
      
      const answer = await peerConnectionRef.current.createAnswer();
      console.log('Answer created:', answer.type);
      
      await peerConnectionRef.current.setLocalDescription(answer);
      console.log('Local description set from answer');

      await sendSignal({
        type: 'answer',
        answer: answer,
        from: userDisplayName
      });
      console.log('✅ Answer sent to signaling server');
    } catch (err) {
      console.error('❌ Error handling offer:', err);
      console.error('Signaling state at error:', peerConnectionRef.current?.signalingState);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      console.log('📥 Handling answer...', answer.type);
      console.log('Current signaling state:', peerConnectionRef.current.signalingState);
      
      // CRITICAL: Check if we're in the right state to receive an answer
      if (peerConnectionRef.current.signalingState !== 'have-local-offer') {
        console.warn('⚠️ Ignoring answer - not in have-local-offer state. Current state:', peerConnectionRef.current.signalingState);
        console.warn('This usually means the answer was already processed or no offer was sent');
        return;
      }
      
      await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('✅ Remote description set from answer - connection should establish now');
      console.log('New signaling state:', peerConnectionRef.current.signalingState);
    } catch (err) {
      console.error('❌ Error handling answer:', err);
      console.error('Signaling state at error:', peerConnectionRef.current?.signalingState);
    }
  };

  const handleIceCandidate = async (candidate) => {
    try {
      console.log('🧊 Adding ICE candidate');
      await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error('❌ Error adding ICE candidate:', err);
    }
  };

  const toggleAudio = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoMuted(!videoTrack.enabled);
      }
    }
  };

  const cleanup = () => {
    console.log('🧹 Cleaning up video call...');

    // Transcript saving functionality removed

    // Audio recording functionality removed

    // Stop polling FIRST to prevent any race conditions
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('⏹️ Polling interval cleared');
    }

    // AGGRESSIVE APPROACH 1: Stop all tracks from BOTH sources
    console.log('🎥 AGGRESSIVE CLEANUP - Stopping ALL media tracks...');
    
    // Get streams from all possible sources
    const streams = [];
    if (localStreamRef.current) streams.push(localStreamRef.current);
    if (localVideoRef.current?.srcObject) streams.push(localVideoRef.current.srcObject);
    if (remoteVideoRef.current?.srcObject) streams.push(remoteVideoRef.current.srcObject);
    
    // Stop ALL tracks from ALL streams
    streams.forEach((stream, index) => {
      if (stream && stream.getTracks) {
        console.log(`🛑 Stopping tracks from stream ${index + 1}:`, stream.id);
        const tracks = stream.getTracks();
        console.log(`  Found ${tracks.length} tracks in this stream`);
        
        tracks.forEach(track => {
          console.log(`  🎬 Stopping ${track.kind} track:`, track.label);
          console.log(`     Before: enabled=${track.enabled}, readyState=${track.readyState}`);
          
          // Force disable and stop
          track.enabled = false;
          track.stop();
          
          console.log(`     After: enabled=${track.enabled}, readyState=${track.readyState}`);
        });
      }
    });

    // AGGRESSIVE APPROACH 2: Get tracks directly from getUserMedia API
    console.log('🔍 Checking for any getUserMedia streams globally...');
    if (window.localStreamGlobal) {
      console.log('Found global stream reference, stopping...');
      window.localStreamGlobal.getTracks().forEach(track => {
        console.log(`  Stopping global ${track.kind}:`, track.readyState);
        track.stop();
      });
      window.localStreamGlobal = null;
    }

    // AGGRESSIVE APPROACH 3: Clear video elements completely
    console.log('🎬 Clearing video elements...');
    if (localVideoRef.current) {
      const localStream = localVideoRef.current.srcObject;
      if (localStream) {
        localStream.getTracks().forEach(track => {
          console.log(`  Force stopping local video track: ${track.kind}`);
          track.stop();
        });
      }
      localVideoRef.current.pause();
      localVideoRef.current.srcObject = null;
      localVideoRef.current.load();
      // Try to remove src attribute too
      localVideoRef.current.removeAttribute('src');
      console.log('✅ Local video element aggressively cleared');
    }

    if (remoteVideoRef.current) {
      const remoteStream = remoteVideoRef.current.srcObject;
      if (remoteStream) {
        remoteStream.getTracks().forEach(track => {
          console.log(`  Force stopping remote video track: ${track.kind}`);
          track.stop();
        });
      }
      remoteVideoRef.current.pause();
      remoteVideoRef.current.srcObject = null;
      remoteVideoRef.current.load();
      remoteVideoRef.current.removeAttribute('src');
      console.log('✅ Remote video element aggressively cleared');
    }

    // Clear refs
    localStreamRef.current = null;
    console.log('✅ Local stream ref cleared');

    // Close peer connection LAST
    if (peerConnectionRef.current) {
      console.log('🔌 Closing peer connection...');
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      console.log('✅ Peer connection closed');
    }

    console.log('✅ AGGRESSIVE cleanup complete - all tracks force stopped');
    console.log('🎥 Camera should be OFF now - if not, check browser permissions');
  };

  const handleEndCall = async () => {
    console.log('🔴 End Call button clicked');
    
    try {
      // Send end-call signal to other party BEFORE cleanup
      console.log('📤 Sending end-call signal...');
      await sendSignal({
        type: 'end-call',
        from: userDisplayName
      });
      console.log('✅ End-call signal sent');
      
      // Log video call completion to backend
      if (videoCallId && consultationId) {
        try {
          console.log('📊 Logging video call completion to backend...');
          await admitBrowserDocument(API, sessionStorage.getItem('token'));
          const response = await fetch(`${API}/video-calls/${videoCallId}/end`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('token')}`, ...documentHeaders()
            },
            body: JSON.stringify({
              consultation_id: consultationId,
              started_at: callStartTimeRef.current || new Date().toISOString()
            })
          });
          
          if (response.ok) {
            console.log('✅ Video call logged successfully');
          } else {
            console.warn('⚠️ Failed to log video call:', await response.text());
          }
        } catch (logError) {
          console.error('❌ Error logging video call:', logError);
        }
      }
      
      // Wait a moment to ensure signal is sent
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('❌ Error sending end-call signal:', error);
    }
    
    // CRITICAL: Call cleanup IMMEDIATELY to stop camera
    console.log('🎯 Calling cleanup IMMEDIATELY to stop camera...');
    cleanup();
    
    // Now wait for React to settle
    console.log('⏳ Waiting 2 seconds for React to settle...');
    console.log('⏳ Camera should ALREADY be OFF - waiting for UI update...');
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('✅ Wait complete, unmounting video call component');
    
    // Double-check: Force stop any remaining tracks one more time
    setTimeout(() => {
      console.log('🔍 FINAL CHECK - Stopping any lingering tracks...');
      
      // Check video elements
      if (localVideoRef.current?.srcObject) {
        const stream = localVideoRef.current.srcObject;
        stream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            console.warn('⚠️ Found lingering LOCAL track, force stopping:', track.kind, track.label);
            track.stop();
          }
        });
      }
      
      if (remoteVideoRef.current?.srcObject) {
        const stream = remoteVideoRef.current.srcObject;
        stream.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            console.warn('⚠️ Found lingering REMOTE track, force stopping:', track.kind);
            track.stop();
          }
        });
      }
      
      // Check global reference
      if (window.localStreamGlobal) {
        window.localStreamGlobal.getTracks().forEach(track => {
          if (track.readyState === 'live') {
            console.warn('⚠️ Found lingering GLOBAL track, force stopping:', track.kind);
            track.stop();
          }
        });
        window.localStreamGlobal = null;
      }
      
      console.log('✅ Final track check complete - camera MUST be off now');
    }, 500);
    
    // Ensure video elements are cleared
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
      console.log('Local video element cleared');
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
      console.log('Remote video element cleared');
    }
    
    if (onCallEnd) {
      onCallEnd();
    }
  };

  if (error) {
    return (
      <div className="w-full h-full bg-gray-900 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Call Error</h3>
          <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">{error}</p>
          <button
            onClick={handleEndCall}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5" />
          <div>
            <p className="font-semibold">
              {isConnected ? '🟢 Connected' : isConnecting ? '⏳ Connecting...' : '⚪ Disconnected'}
            </p>
            {remoteUserConnected && <p className="text-xs">Participant joined</p>}
          </div>
        </div>
        
        <button
          onClick={handleEndCall}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors"
        >
          <PhoneOff className="w-4 h-4" />
          End Call
        </button>
      </div>

      {/* Video Layout - Side by Side */}
      <div className="flex-1 flex gap-2 p-2 overflow-hidden">
        {/* Video Container */}
        <div className="w-full flex gap-2 transition-all">
          {/* Local Video (Left Side) */}
          <div className="w-1/2 bg-black rounded-lg overflow-hidden relative h-full">
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-contain"
          />
          <div className="absolute bottom-2 left-2 text-white text-sm bg-black bg-opacity-50 px-2 py-1 rounded">
            You ({userDisplayName})
          </div>
        </div>

        {/* Remote Video (Right Side) */}
        <div className="w-1/2 bg-black rounded-lg overflow-hidden relative h-full">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-contain"
          />
          {!remoteUserConnected && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="text-center text-white">
                <Loader className="w-12 h-12 animate-spin mx-auto mb-2" />
                <p>Waiting for other participant...</p>
              </div>
            </div>
          )}
          {remoteUserConnected && (
            <div className="absolute bottom-2 left-2 text-white text-sm bg-black bg-opacity-50 px-2 py-1 rounded">
              Other Participant
            </div>
          )}
        </div>
      </div>

        {/* Transcript panel removed */}
      </div>

      {/* Controls */}
      <div className="bg-gray-800 p-4 flex items-center justify-center gap-4">
        <button
          onClick={toggleAudio}
          className={`p-4 rounded-full transition-colors ${
            isAudioMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isAudioMuted ? 'Unmute Microphone' : 'Mute Microphone'}
        >
          {isAudioMuted ? (
            <MicOff className="w-6 h-6 text-white" />
          ) : (
            <Mic className="w-6 h-6 text-white" />
          )}
        </button>

        <button
          onClick={toggleVideo}
          className={`p-4 rounded-full transition-colors ${
            isVideoMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isVideoMuted ? 'Turn On Camera' : 'Turn Off Camera'}
        >
          {isVideoMuted ? (
            <VideoOff className="w-6 h-6 text-white" />
          ) : (
            <Video className="w-6 h-6 text-white" />
          )}
        </button>

        {/* Transcript toggle removed */}
      </div>

      {/* DPDPA Compliance Notice */}
      <div className="bg-blue-900 bg-opacity-90 text-white px-4 py-2 text-xs flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span>Peer-to-peer encrypted • DPDPA 2023 compliant • Not recorded</span>
        </div>
        <span className="text-gray-300">INSIGHT Platform</span>
      </div>
    </div>
  );
};

export default WebRTCVideoCall;
